import { MutableRefObject, useRef } from "react";

interface VisibilityOpts {
  useWindowHeight?: boolean;
  topOffset?: number;
  bottomOffset?: number;
}

interface ScrollIntoViewOptions {
  behavior?: "auto" | "smooth";
  block?: "start" | "center" | "end" | "nearest";
  inline?: "start" | "center" | "end" | "nearest";
  /** If true, will initiate scroll even if the element is already visible */
  force?: boolean;
  visibilityOpts?: VisibilityOpts;
}

/** Get top and bottom position of element */
export const getElementPosition = (
  element: HTMLElement | null,
  visibilityOpts?: VisibilityOpts
) => {
  if (!element || visibilityOpts?.useWindowHeight) {
    return {
      top: 0,
      bottom: visibilityOpts?.useWindowHeight ? window.innerHeight : 0,
    };
  }

  const { top: elementTop, bottom: elementBottom } =
    element.getBoundingClientRect();

  return {
    top: elementTop + (visibilityOpts?.topOffset ?? 0),
    bottom: elementBottom - (visibilityOpts?.bottomOffset ?? 0),
  };
};

/**
 * Determine if element is visible based on its position relative to the parent element
 * (primary use case is for children inside long, scrollable elements). Children that are
 * shorter than a parent will only be considered "visible" if the entire element is visible,
 * otherwise an element will be considered visible if the top is visible.
 * TODO (ENG-6826) - See if any of this logic can be replaced with an IntersectionObserver
 * */
export const elementIsVisible = (
  element: HTMLElement | null,
  parent: HTMLElement | null,
  visibilityOpts?: VisibilityOpts
) => {
  if (!element) return false;

  const { top: elementTop, bottom: elementBottom } =
    getElementPosition(element);

  const { top: parentTop, bottom: parentBottom } = getElementPosition(
    parent,
    visibilityOpts
  );

  const elementHeight = elementBottom - elementTop;
  const parentHeight = parentBottom - parentTop;

  const elementTopIsVisible =
    elementTop >= parentTop && elementTop <= parentBottom;

  if (elementHeight < parentHeight) {
    const elementBottomIsVisible = elementBottom <= parentBottom;
    return elementTopIsVisible && elementBottomIsVisible;
  }

  return elementTopIsVisible;
};

export default <Element extends HTMLElement, Parent extends HTMLElement>(
  parent: MutableRefObject<Parent | null>
) => {
  const currentElementRef = useRef<Element>(null);

  const isVisible = (opts?: VisibilityOpts) => {
    if (!currentElementRef?.current) return false;

    const parentElement = opts?.useWindowHeight ? null : parent.current;
    return elementIsVisible(currentElementRef.current, parentElement, opts);
  };

  const scrollIntoView = (opts?: ScrollIntoViewOptions) => {
    if (!currentElementRef.current) return;

    if (opts?.force || !isVisible(opts?.visibilityOpts)) {
      currentElementRef.current.scrollIntoView(opts);
    }
  };

  return { isVisible, ref: currentElementRef, scrollIntoView };
};
